import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Pagination.module.scss";
import skeletonStyles from "./Table.module.scss";

import MultiSelectDropdownInput from "@/components/forms/inputs/MultiSelectDropdownInput";
import { DropdownOption } from "@/components/forms/inputs/MultiSelectDropdownInput";
import Icons from "@/components/icons";
import { cn } from "@/lib/utils/cn";
import { calculateVisiblePages } from "@/lib/utils/pagination";

interface PaginatorProps {
  isLoading?: boolean;
  itemsPerPage: number;
  page: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage?: (itemsPerPage: number) => void;
  totalItems: number;
}

function Paginator({
  isLoading,
  itemsPerPage,
  page,
  setCurrentPage,
  setItemsPerPage,
  totalItems,
}: PaginatorProps) {
  const { t } = useTranslation();
  const itemOffset = ((page - 1) * itemsPerPage);
  const endOffset = itemOffset + itemsPerPage;
  const pageCount = Math.ceil(totalItems / itemsPerPage);
  const startItem = itemOffset + 1;
  const endItem = Math.min(endOffset, totalItems);
  const visiblePages = useMemo(
    () => calculateVisiblePages(page, pageCount),
    [page, pageCount]
  );

  const itemsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  const goToPage = (target: number) => {
    if (target < 1 || target > pageCount || target === page) {
      return;
    }
    setCurrentPage(target);
  };

  const isFirstPage = page <= 1;
  const isLastPage = page >= pageCount;

  return (
    <div className={styles.pagination}>
      {pageCount > 0 ? (
        <nav aria-label="pagination">
          <ul className={styles.paginationContainer}>
            <li className={cn(styles.previousButton, isFirstPage && styles.disabledButton)}>
              <button
                type="button"
                className={styles.pageLink}
                onClick={() => goToPage(page - 1)}
                disabled={isFirstPage}
                aria-label={t('common.previous')}
              >
                <Icons.ChevronLeftIcon className={styles.icon} fontSize={24} />
                <span>{t('common.previous')}</span>
              </button>
            </li>

            {visiblePages.map((pageItem, index) =>
              pageItem === "ellipsis" ? (
                <li key={`ellipsis-${index}`} className={styles.breakButton} aria-hidden>
                  <span className={styles.pageLink}>...</span>
                </li>
              ) : (
                <li
                  key={pageItem}
                  className={cn(styles.pageButton, pageItem === page && styles.activeButton)}
                >
                  <button
                    type="button"
                    className={styles.pageLink}
                    onClick={() => goToPage(pageItem)}
                    aria-current={pageItem === page ? "page" : undefined}
                  >
                    {pageItem}
                  </button>
                </li>
              )
            )}

            <li className={cn(styles.nextButton, isLastPage && styles.disabledButton)}>
              <button
                type="button"
                className={styles.pageLink}
                onClick={() => goToPage(page + 1)}
                disabled={isLastPage}
                aria-label={t('common.next')}
              >
                <span>{t('common.next')}</span>
                <Icons.ChevronRightIcon className={styles.icon} fontSize={24} />
              </button>
            </li>
          </ul>
        </nav>
      )
        : isLoading ? (
          <div className={skeletonStyles.skeletonCell}>
            <div style={{
              width: '100%',
              minWidth: '365px',
              maxWidth: '365px'
            }} />
          </div>
        )
          : null}
      <div className={styles.paginationInfo}>
        {totalItems > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>
                  {t('pagination.itemsDisplayPrefix', { start: startItem })}
                </span>
                <div style={{ minWidth: '80px' }}>
                  <MultiSelectDropdownInput
                    mode="single"
                    size="small"
                    name="itemsPerPage"
                    options={itemsPerPageOptions}
                    value={itemsPerPageOptions.find(option => option.value === itemsPerPage) ?? null}
                    postfix={<Icons.ArrowDropDownIcon fontSize={24} />}
                    onChange={(value: DropdownOption<number> | DropdownOption<number>[] | null) => {
                      if (setItemsPerPage) {
                        if (Array.isArray(value)) {
                          throw new Error("Single mode is not supported for items per page");
                        }
                        setItemsPerPage?.(value?.value ?? 0);
                      }
                    }}
                  />
                </div>
                <span>
                  {t('pagination.itemsDisplay', { start: startItem, end: endItem, total: totalItems })}
                </span>
              </div>
            </div>
          </div>
        ) : isLoading && (
          <div className={skeletonStyles.skeletonCell}>
            <div style={{
              width: '100%',
              minWidth: '147px',
              maxWidth: '147px'
            }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Paginator;
